// © Microsoft Corporation. All rights reserved.

/**
 * This file defines the datatypes used in telemetry beacons.
 */

/**
 * Telemetry Object Type. (Deprecating. Please use TelemetryContentType and TelemetryBehaviorType instead)
 * @enum TelemetryObjectType
 */
export const TelemetryType = {
    Undefined: 0,
    Header: 1,
    Footer: 2,
    TopNavBar: 3,
    NavBar: 4,
    DropDown: 5,
    Section: 6,
    Carousel: 7,
    Module: 8,
    SearchBox: 9,
    InputBox: 10,
    ActionButton: 11,
    Interaction: 12,
    Headline: 13,
    Entity: 15,
    Topic: 29,
    Provider: 30,
    NewStories: 46,
    AutoSuggest: 47,
    Tile: 49,
    TrendingNews: 50,
    AllCoverage: 51,
    Report: 52,
    MsccCookieComplianceBanner: 54,
    CookieWallCookieComplianceBanner: 57,
    FRE: 60,
    VideoPlayer: 61,
    ContentCard: 62,
    SubmitForm: 63
};

/**
 * Telemetry Content Type used to describe content type that is assocated with an object
 * @enum TelemetryContentType
 */
export const TelemetryContentType = {
    Undefined: 0,
    StructuredData: 1,
    AppStore: 2,
    CmsPromotions: 3,
    MicrosoftWebStore: 4,
    XboxStore: 5,
    OfficeStore: 6,
    PartnerLinks: 7,
    SponsoredContent: 8,
    Spotlight: 9,
    ExternalLink: 10,
    SearchPromo: 11,
    Feed: 12,
    Article: 13,
    Gallery: 14,
    Video: 15,
    Audio: 16,
    Settings: 17,
    DisplayAd: 18,
    OfficeDocument: 19,
    App: 20,
    SearchResult: 21,
    VideoAd: 22,
    Map: 23,
    Comment: 24,
    Profile: 25,
    Coachmark: 26,
    TrendingSearch: 27,
    AutoSuggestSearch: 28,
    SearchHistory: 29,
    TextSearch: 30,
    TopSites: 31,
    SearchLink: 32,
    ImageSearch: 33,
    VoiceSearch: 34,
    Toast: 35,
    Greeting: 36,
    Image: 37,
    Banner: 38,
    Waffle: 39
};

/**
 * Telemetry Action Type
 * @enum TelemetryActionType
 */
export const TelemetryActionType = {
    Click: "click",
    Hover: "mouseenter",
    Submit: "submit",
    View: "view",
    RightClick: "rightclick",
    Wheel: "wheel",
    KeyPress: "keypress",
    HorizontalSwipe: "hswipe"
};

/**
 * Telemetry Behavior Type
 * @enum TelemetryBehaviorType
 */
export const TelemetryBehaviorType = {
    Undefined: 0,
    Navigate: 1,
    ContextMenu: 2,
    Pin: 3,
    Unpin: 4,
    Rearrange: 5,
    Remove: 6,
    Add: 7,
    Open: 8,
    View: 9,
    Follow: 10,
    Unfollow: 11,
    More: 12,
    Close: 13,
    Share: 14,
    Like: 15,
    Dislike: 16,
    Unlike: 17,
    Undislike: 18,
    Mute: 19,
    Unmute: 20,
    Cancel: 21,
    Launch: 22,
    Install: 23,
    Manage: 24,
    Show: 25,
    MuteCancel: 26,
    Save: 27,
    Hide: 28,
    Unhide: 29,
    Customize: 30,
    Suspend: 31,
    Clear: 32,
    SignIn: 33,
    SignOut: 34,
    UndoSave: 35,
    TurnOn: 36,
    TurnOff: 37,
    Forward: 38,
    Response: 39,
    Report: 40,
    ZoomIn: 41,
    ZoomOut: 42,
    Comment: 43,
    Sort: 44,
    Love: 45,
    Surprised: 46,
    Thinking: 47,
    Sad: 48,
    Angry: 49,
    TextSearch: 50,
    ImageSearch: 51,
    UndoRemove: 52,
    Rename: 53,
    SearchLink: 54,
    VoiceSearch: 55,
    Complete: 56,
    Play: 57,
    Pause: 58,
    Seek: 59,
    Volume: 60,
    ClosedCaptions: 61,
    Fullscreen: 62,
    Restore: 63
};

/**
 * Telemetry Content Source Type
 * @enum TelemetryContentSourceType
 */
export const TelemetryContentSourceType = {
    Editor: 0,
    PersonalizationAlgo: 1,
    Bing: 2,
    Taboola: 3,
    Outbrain: 4,
    AutoCuration: 5,
    User: 6,
    AppNexus: 7,
    OtherSponconProvider: 8
};

/**
 * Telemetry Page Configuration
 * @enum TelemetryPageConfiguration
 */
export const TelemetryPageConfiguration = {
    Empty: 0,
    SearchBox: 1,
    TopSites: 2,
    Suggested: 4,
    ContentFeed: 8,
    WinFRE: 16,
    BingImage: 32,
    ContentBelowTheFold: 64,
    CustomModeOption: 128,
    GreetingEnabled: 256,
    HeadingsOnly: 512,
    StripeLayout: 1024,
    Peek: 2048
};

/**
 * Mouse click number to identify right/left/middle clicks
 */
export const MouseButton = {
    LeftClickButton: 0,
    MiddleClickButton: 1,
    RightClickButton: 2
};

/**
 * Telemetry Page Type
 * @type TelemetryTelemetryPageType
 */
export type TelemetryPageType = "unspecified" | "dhp" | "ntp" | "hp" | "verthp" | "article" | "gallery" | "video" | "spotlight" | "qna-feed";

/**
 * Telemetry Page Product
 * @type TelemetryPageProduct
 */
export type TelemetryPageProduct = "emmx" | "prime" | "spartan" | "chromeext" | "ieext" | "anaheim" | "amp" | "qna" | "entnews" | "prime_mobile" | "msnews" | "xbox" | "chromentpnews" | "windowsshell" | "esportshub" | "shoppingext" | "entwindowsshell" | "windowsdash" | "launcher" | "qnafeed";

/**
 * Telemetry Service Type
 * @type TelemetryServiceType
 */
export type TelemetryServiceType = "oneservice";

/**
 * Telemetry audience mode
 * @type TelemetryAudienceMode
 */
export type TelemetryAudienceMode = "enterprise" | "consumer";

/**
 * Log type.
 * @type LogType
 */
export type LogType = "information" | "error" | "warning";

/**
 * ANID cookie name.
 * @type ANONCookieName
 */
export type ANONCookieName = "anon" | "app_anon";

/**
 * Feed layout type.
 * @type FeedLayout
 */
export type FeedLayout = "singlecol";